import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ArrowUp, ArrowDown, Star, RefreshCw } from "lucide-react";
import { useLang, useL } from "@/lib/i18n";
import { api, errDetail, fileUrl } from "@/lib/api";
import { F, Card, GhostBtn } from "./ui";

function PhotoItem({ p, i, total, onEdit, onAction }) {
  const L = useL();
  const icon = "rounded-full border border-white/10 p-2 text-slate-300 transition-colors hover:border-white/40 hover:text-white disabled:opacity-30";
  return (
    <div className="rounded-xl border border-white/10 p-3" data-testid={`admin-photo-${i}`}>
      <div className="relative mb-3 flex items-center justify-center rounded-lg bg-black/40">
        <img src={fileUrl(p.thumb_path || p.storage_path)} alt="" className="h-48 w-auto max-w-full object-contain" />
        {p.is_hero && <span className="absolute left-2 top-2 rounded-full bg-red-600 px-2 py-0.5 font-mono text-[10px] uppercase text-white" data-testid={`photo-hero-badge-${i}`}>{L("Image principale", "Main image")}</span>}
      </div>
      <div className="mb-3 flex flex-wrap gap-2">
        <button className={icon} disabled={i === 0} onClick={() => onAction("up")} data-testid={`photo-up-${i}`} aria-label="Up"><ArrowUp size={16} /></button>
        <button className={icon} disabled={i === total - 1} onClick={() => onAction("down")} data-testid={`photo-down-${i}`} aria-label="Down"><ArrowDown size={16} /></button>
        <button className={icon} disabled={p.is_hero} onClick={() => onAction("hero")} data-testid={`photo-hero-${i}`} title={L("Définir comme image principale", "Set as main image")}><Star size={16} /></button>
        <label className={`${icon} cursor-pointer`} title={L("Remplacer", "Replace")} data-testid={`photo-replace-${i}`}>
          <RefreshCw size={16} />
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files[0] && onAction("replace", e.target.files[0])} data-testid={`photo-replace-input-${i}`} />
        </label>
        <button className={`${icon} hover:text-red-400`} onClick={() => onAction("delete")} data-testid={`photo-delete-${i}`} aria-label="Delete"><Trash2 size={16} /></button>
      </div>
      <div className="space-y-2">
        <F id={`photo-caption-fr-${i}`} label="Légende FR" value={p.caption_fr || ""} onChange={(e) => onEdit("caption_fr", e.target.value)} />
        <F id={`photo-caption-en-${i}`} label="Caption EN" value={p.caption_en || ""} onChange={(e) => onEdit("caption_en", e.target.value)} />
        <GhostBtn onClick={() => onAction("save")} data-testid={`photo-save-${i}`}>{L("Enregistrer les légendes", "Save captions")}</GhostBtn>
      </div>
    </div>
  );
}

export default function AdminPhotos({ onChange }) {
  const L = useL();
  const { tErr } = useLang();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/photos").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChange?.(); };
  const run = async (fn) => { try { await fn(); done(); } catch (e) { toast.error(tErr(errDetail(e))); } };

  const upload = async (e) => {
    const files = [...e.target.files];
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try { await api.post("/admin/photos", fd); } catch (ex) { toast.error(tErr(errDetail(ex))); }
    }
    setBusy(false); e.target.value = ""; done();
  };
  const action = (p, i) => async (kind, file) => {
    if (kind === "up" || kind === "down") {
      const ids = items.map((x) => x.id);
      const j = kind === "up" ? i - 1 : i + 1;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      return run(() => api.post("/admin/photos/reorder", { ids }));
    }
    if (kind === "hero") return run(() => api.post(`/admin/photos/${p.id}/hero`));
    if (kind === "delete") return window.confirm(L("Supprimer cette photo ?", "Delete this photo?")) && run(() => api.delete(`/admin/photos/${p.id}`));
    if (kind === "save") return run(async () => { await api.put(`/admin/photos/${p.id}`, { caption_fr: p.caption_fr || "", caption_en: p.caption_en || "", order: p.order }); toast.success("OK"); });
    if (kind === "replace") {
      const fd = new FormData();
      fd.append("file", file);
      setBusy(true);
      await run(() => api.post(`/admin/photos/${p.id}/replace`, fd));
      setBusy(false);
    }
  };
  const edit = (id) => (k, v) => setItems(items.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  return (
    <Card title={L("Photos du studio", "Studio photos")} testId="admin-photos"
      right={<label className="btn-red inline-flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"><Upload size={16} />{busy ? "…" : L("Téléverser", "Upload")}
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} data-testid="photo-upload-input" /></label>}>
      <p className="mb-4 text-xs text-slate-500">{L("JPG, PNG ou WEBP, 15 Mo max. Les photos sont optimisées automatiquement sans recadrage. L'étoile choisit l'image principale de l'accueil (elle n'est pas répétée dans la galerie).", "JPG, PNG or WEBP, 15 MB max. Photos are optimized automatically without cropping. The star sets the homepage main image (it is not repeated in the gallery).")}</p>
      {!items.length && <p className="text-sm text-slate-500">{L("Aucune photo.", "No photos yet.")}</p>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p, i) => <PhotoItem key={p.id} p={p} i={i} total={items.length} onEdit={edit(p.id)} onAction={action(p, i)} />)}
      </div>
    </Card>
  );
}
